import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock3,
  ShieldCheck,
  Lock,
  FileText,
  BadgeCheck,
  Eye,
  Globe,
  User,
  FileSignature,
  Layers3,
  ScanSearch,
  Info,
  CheckCircle2,
  ChevronDown,
  Fingerprint,
  Hash,
  Key,
  KeyRound,
  Shield,
} from "lucide-react";

const steps = [
  "Upload",
  "Configure Digital Identity",
  "Choose Mode",
  "Advanced Options",
  "Customize Signature",
  "Review & Sign",
];

const STEP_TONES = {
  done: { circle: "bg-green-500 border-green-500 text-white", label: "text-green-600" },
  current: { circle: "bg-indigo-600 border-indigo-600 text-white", label: "text-indigo-600" },
  upcoming: { circle: "bg-white border-gray-300 text-gray-500", label: "text-gray-500" },
};

function getStepTone(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

function Stepper({ steps: stepList, currentStep }) {
  return (
    <div className="flex items-center justify-between mb-4 h-[72px]">
      {stepList.map((step, index) => {
        const tone = getStepTone(index, currentStep);
        const isDone = tone === "done";
        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== stepList.length - 1 && (
              <div
                className={`absolute top-5 left-1/2 w-full h-[2px] ${
                  isDone ? "bg-green-500" : "bg-gray-300"
                }`}
              />
            )}
            <div
              className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${STEP_TONES[tone].circle}`}
            >
              {isDone ? <Check size={16} strokeWidth={3} className="text-white" /> : index + 1}
            </div>
            <p
              className={`mt-1 text-sm font-medium text-center hidden sm:block ${STEP_TONES[tone].label}`}
            >
              {step}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function resolveSigningContext(state) {
  const identity = state.identity || null;
  const selectedDigitalId = state.selectedDigitalId || null;
  return {
    identity,
    selectedDigitalId,
    uploadedFile: state.uploadedFile || null,
    documentData: state.documentData || null,
    uploadedFiles: state.uploadedFiles || [],
    algorithm:
      selectedDigitalId?.algorithm ?? identity?.algorithm ?? state.algorithm ?? "Dilithium3",
    signingMode:
      selectedDigitalId?.mode ??
      selectedDigitalId?.signingMode ??
      state.mode ??
      "PQ",
  };
}

function buildSummaryItems({ uploadedFile, documentData, identity, signingMode, algorithm }) {
  return [
    { label: "Document", value: uploadedFile?.name || documentData?.name || "No document selected", Icon: FileText },
    { label: "Identity Source", value: identity?.source || identity?.name || "Not specified", Icon: Key },
    { label: "Certificate", value: identity?.certificate || "Not specified", Icon: BadgeCheck },
    { label: "Identity Type", value: identity?.type || "Not specified", Icon: User },
    { label: "Signing Mode", value: signingMode, Icon: Shield },
    { label: "Algorithm", value: algorithm, Icon: Fingerprint },
    { label: "Hash Algorithm", value: identity?.hashAlgorithm || "SHA3-512", Icon: Hash },
    { label: "Trust Level", value: identity?.trustLevel || signingMode, Icon: KeyRound, badge: true },
  ];
}

function getValidationError(toggles, documentEncryptionEnabled, encryptionPassword) {
  if (!toggles.some(Boolean)) {
    return "Please enable at least one option before proceeding.";
  }
  if (documentEncryptionEnabled && !encryptionPassword.trim()) {
    return "Please provide an encryption password to enable document encryption.";
  }
  return null;
}

function useScrollTopAndOutsideClick(ref, onOutsideClick) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector("main")?.scrollTo(0, 0);
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onOutsideClick();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function OptionCard({
  icon,
  title,
  description,
  enabled,
  onToggle,
  children,
  footerOn,
  footerOff,
}) {
  const footerText = enabled ? footerOn : footerOff;
  const footerClass = enabled ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700";
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 flex flex-col">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            {icon}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
            </div>
            <p className="text-[12px] text-gray-500 mt-1">{description}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={onToggle}
          className={`w-11 h-6 rounded-full relative shrink-0 transition ${
            enabled ? "bg-violet-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${
              enabled ? "left-5" : "left-0.5"
            }`}
          />
        </button>
      </div>

      <div className="mt-4 space-y-3 flex-1">{children}</div>

      {footerText ? (
        <div
          className={`mt-4 rounded-lg px-3 py-2 text-[12px] flex items-center gap-2 ${footerClass}`}
        >
          <CheckCircle2 size={14} />
          <span>{footerText}</span>
        </div>
      ) : null}
    </div>
  );
}

function SelectField({ label, options, value, onChange }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-900 outline-none focus:ring-2 focus:ring-violet-500"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
      </div>
    </div>
  );
}

export default function AdvancedOptionsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state || {};

  const {
    identity,
    selectedDigitalId,
    uploadedFile,
    documentData,
    uploadedFiles,
    algorithm,
    signingMode,
  } = resolveSigningContext(state);

  const currentStep = 3;
  const [, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);

  useScrollTopAndOutsideClick(profileRef, () => setShowProfileMenu(false));

  // Card Toggles
  const [timestampEnabled, setTimestampEnabled] = useState(true);
  const [blockchainEnabled, setBlockchainEnabled] = useState(true);
  const [documentEncryptionEnabled, setDocumentEncryptionEnabled] = useState(false);
  const [certificateEmbeddingEnabled, setCertificateEmbeddingEnabled] = useState(true);
  const [revocationCheckEnabled, setRevocationCheckEnabled] = useState(true);
  const [signaturePolicyEnabled, setSignaturePolicyEnabled] = useState(true);
  const [documentLockEnabled, setDocumentLockEnabled] = useState(true);
  const [visualSealEnabled, setVisualSealEnabled] = useState(true);
  const [signerAttributeEnabled, setSignerAttributeEnabled] = useState(true);
  const [ipRestrictionEnabled, setIpRestrictionEnabled] = useState(false);
  const [signingReasonEnabled, setSigningReasonEnabled] = useState(true);

  // Dynamic Form Selects & Inputs
  const [timestampAuthority, setTimestampAuthority] = useState("Intrust TSA (Quantum Safe)");
  const [timestampHashAlgo, setTimestampHashAlgo] = useState("SHA-3 512 (Recommended)");
  const [blockchainType, setBlockchainType] = useState("Ethereum (Mainnet)");
  const [blockchainNetwork, setBlockchainNetwork] = useState("Mainnet");
  const [encryptionAlgorithm, setEncryptionAlgorithm] = useState("AES-256-GCM");
  const [encryptionPassword, setEncryptionPassword] = useState("");
  const [certificateChainOption, setCertificateChainOption] = useState("Full Chain (Recommended)");
  const [revocationMethod, setRevocationMethod] = useState("OCSP (Online)");
  const [revocationFallback, setRevocationFallback] = useState("Fail Signing");
  const [policyType, setPolicyType] = useState("PQSign Default Policy");

  const handleNext = () => {
    const validationError = getValidationError(
      [
        timestampEnabled,
        blockchainEnabled,
        documentEncryptionEnabled,
        certificateEmbeddingEnabled,
        revocationCheckEnabled,
        signaturePolicyEnabled,
        documentLockEnabled,
        visualSealEnabled,
        signerAttributeEnabled,
        ipRestrictionEnabled,
        signingReasonEnabled,
      ],
      documentEncryptionEnabled,
      encryptionPassword
    );

    if (validationError) {
      alert(validationError);
      return;
    }

    navigate("/customize-signature", {
      state: {
        ...state,
        identity,
        selectedDigitalId,
        uploadedFile,
        documentData,
        uploadedFiles,
        algorithm,
        signingMode,
        timestampEnabled,
        timestampAuthority,
        timestampHashAlgo,
        blockchainEnabled,
        blockchainType,
        blockchainNetwork,
        documentEncryptionEnabled,
        encryptionAlgorithm,
        encryptionPassword,
        certificateEmbeddingEnabled,
        certificateChainOption,
        revocationCheckEnabled,
        revocationMethod,
        revocationFallback,
        signaturePolicyEnabled,
        policyType,
        documentLockEnabled,
        visualSealEnabled,
        signerAttributeEnabled,
        ipRestrictionEnabled,
        signingReasonEnabled,
        documentId: state.documentId,
      },
    });
  };

  const summaryItems = buildSummaryItems({
    uploadedFile,
    documentData,
    identity,
    signingMode,
    algorithm,
  });

  return (
    <div className="w-full bg-white min-h-screen">
      <div className="w-full px-0 py-0">
        {/* STEPPER */}
        <Stepper steps={steps} currentStep={currentStep} />

        {/* MAIN GRID */}
        <div className="flex flex-col gap-5">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-stretch">
            {/* LEFT COLUMN - MAIN OPTIONS */}
            <div className="space-y-5 flex flex-col">
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch flex-1">
                {/* Timestamping */}
                <OptionCard
                  icon={<Clock3 size={18} />}
                  title="Timestamping"
                  description="Add a trusted timestamp to prove when the document was signed."
                  enabled={timestampEnabled}
                  onToggle={() => setTimestampEnabled(!timestampEnabled)}
                  footerOn="Timestamp will be embedded"
                  footerOff="Timestamp disabled"
                >
                  <SelectField
                    label="Timestamp Authority"
                    value={timestampAuthority}
                    onChange={(e) => setTimestampAuthority(e.target.value)}
                    options={["Intrust TSA (Quantum Safe)", "Global Trust TSA", "Secure Timestamp"]}
                  />
                  <SelectField
                    label="Hash Algorithm"
                    value={timestampHashAlgo}
                    onChange={(e) => setTimestampHashAlgo(e.target.value)}
                    options={["SHA-3 512 (Recommended)", "SHA-256", "SHA-512"]}
                  />
                </OptionCard>

                {/* Blockchain Anchoring */}
                <OptionCard
                  icon={<Layers3 size={18} />}
                  title="Blockchain Anchoring"
                  description="Anchor document hash to blockchain for tamper-proof verification."
                  enabled={blockchainEnabled}
                  onToggle={() => setBlockchainEnabled(!blockchainEnabled)}
                  footerOn="Document hash will be anchored"
                  footerOff="Anchoring disabled"
                >
                  <SelectField
                    label="Select Blockchain"
                    value={blockchainType}
                    onChange={(e) => setBlockchainType(e.target.value)}
                    options={["Ethereum (Mainnet)", "QuantumChain (Permissioned)", "Hyperledger"]}
                  />
                  <SelectField
                    label="Network"
                    value={blockchainNetwork}
                    onChange={(e) => setBlockchainNetwork(e.target.value)}
                    options={["Mainnet", "Testnet", "Private Network"]}
                  />
                </OptionCard>

                {/* Document Encryption */}
                <OptionCard
                  icon={<Lock size={18} />}
                  title="Document Encryption"
                  description="Encrypt document after signing for confidentiality."
                  enabled={documentEncryptionEnabled}
                  onToggle={() => setDocumentEncryptionEnabled(!documentEncryptionEnabled)}
                  footerOn="Document will be encrypted"
                  footerOff="Encryption is disabled"
                >
                  <SelectField
                    label="Encryption Algorithm"
                    value={encryptionAlgorithm}
                    onChange={(e) => setEncryptionAlgorithm(e.target.value)}
                    options={["AES-256-GCM", "ChaCha20-Poly1305", "AES-128-GCM"]}
                  />
                  <div>
                    <label className="block text-[12px] font-medium text-gray-700 mb-1">
                      Password / Key
                    </label>
                    <input
                      type="password"
                      value={encryptionPassword}
                      onChange={(e) => setEncryptionPassword(e.target.value)}
                      placeholder="Enter encryption password"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                </OptionCard>

                {/* Certificate Embedding */}
                <OptionCard
                  icon={<BadgeCheck size={18} />}
                  title="Certificate Embedding"
                  description="Embed signer certificate in the signed document."
                  enabled={certificateEmbeddingEnabled}
                  onToggle={() => setCertificateEmbeddingEnabled(!certificateEmbeddingEnabled)}
                  footerOn="Certificate chain will be embedded"
                  footerOff="Embedding disabled"
                >
                  <SelectField
                    label="Include Certificate Chain"
                    value={certificateChainOption}
                    onChange={(e) => setCertificateChainOption(e.target.value)}
                    options={["Full Chain (Recommended)", "End Entity Only"]}
                  />
                </OptionCard>

                {/* Revocation Check */}
                <OptionCard
                  icon={<ScanSearch size={18} />}
                  title="Revocation Check"
                  description="Validate certificate status at signing time."
                  enabled={revocationCheckEnabled}
                  onToggle={() => setRevocationCheckEnabled(!revocationCheckEnabled)}
                  footerOn="Certificate status will be validated"
                  footerOff="Validation disabled"
                >
                  <SelectField
                    label="Check Method"
                    value={revocationMethod}
                    onChange={(e) => setRevocationMethod(e.target.value)}
                    options={["OCSP (Online)", "CRL (Offline)", "Both"]}
                  />
                  <SelectField
                    label="If Unavailable"
                    value={revocationFallback}
                    onChange={(e) => setRevocationFallback(e.target.value)}
                    options={["Fail Signing", "Warn Only"]}
                  />
                </OptionCard>

                {/* Signature Policy */}
                <OptionCard
                  icon={<FileSignature size={18} />}
                  title="Signature Policy"
                  description="Apply signature policy for compliance."
                  enabled={signaturePolicyEnabled}
                  onToggle={() => setSignaturePolicyEnabled(!signaturePolicyEnabled)}
                  footerOn="Policy will be applied"
                  footerOff="Policy disabled"
                >
                  <SelectField
                    label="Policy Type"
                    value={policyType}
                    onChange={(e) => setPolicyType(e.target.value)}
                    options={["PQSign Default Policy", "Custom Policy"]}
                  />
                  <button type="button" className="text-[12px] text-indigo-600 font-medium hover:underline text-left">
                    View Policy Details
                  </button>
                </OptionCard>
              </div>
            </div>

            {/* RIGHT COLUMN - SUMMARY & ABOUT */}
            <div className="space-y-5 flex flex-col">
              {/* Signing Summary */}
              <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-4">
                <p className="text-xs font-bold text-gray-900 mb-3">Signing Summary</p>
                <div className="space-y-4">
                  {summaryItems.map(({ label, value, Icon, badge }) => (
                    <div key={label} className="grid grid-cols-2 gap-2 text-[12.5px] items-center">
                      <div className="flex items-center gap-1.5 text-gray-400 min-w-0">
                        <span className="flex-shrink-0">
                          <Icon size={14} className="text-violet-500" />
                        </span>
                        <span className="truncate">{label}</span>
                      </div>
                      {badge ? (
                        <span className="text-[11px] font-bold text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-md text-left inline-block w-fit">
                          {value}
                        </span>
                      ) : (
                        <span className="font-semibold text-gray-800 text-left leading-tight truncate">
                          {value}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* About Advanced Options Card */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 flex-1">
                <h2 className="text-[15px] font-bold text-gray-900 mb-2.5 text-left">About Advanced Options</h2>
                <p className="text-[12px] text-gray-500 leading-relaxed text-left">
                  These options add additional security layers and ensure the integrity, authenticity and compliance of your digitally signed document.
                </p>
                <ul className="mt-5 space-y-3.5 text-[13px] font-semibold text-gray-900">
                  <li className="flex items-center gap-3">
                    <ShieldCheck size={18} className="text-indigo-600 shrink-0" />
                    <span>Stronger security</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <FileSignature size={18} className="text-indigo-600 shrink-0" />
                    <span>Tamper-proof verification</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <BadgeCheck size={18} className="text-indigo-600 shrink-0" />
                    <span>Compliance ready</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Globe size={18} className="text-indigo-600 shrink-0" />
                    <span>Enterprise grade trust</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* FULL WIDTH - 5 TOGGLE CARDS */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            {[
              {
                icon: <Lock size={22} />,
                title: "Document Lock",
                desc: "Prevent document modification",
                enabled: documentLockEnabled,
                toggle: () => setDocumentLockEnabled(!documentLockEnabled),
              },
              {
                icon: <Eye size={22} />,
                title: "Visual Seal",
                desc: "Add a visible seal to indicate signed status.",
                enabled: visualSealEnabled,
                toggle: () => setVisualSealEnabled(!visualSealEnabled),
              },
              {
                icon: <User size={22} />,
                title: "Signer Attribute",
                desc: "Embed signer information and attributes.",
                enabled: signerAttributeEnabled,
                toggle: () => setSignerAttributeEnabled(!signerAttributeEnabled),
              },
              {
                icon: <Globe size={22} />,
                title: "IP Restriction",
                desc: "Restrict signature usage by IP range.",
                enabled: ipRestrictionEnabled,
                toggle: () => setIpRestrictionEnabled(!ipRestrictionEnabled),
              },
              {
                icon: <FileText size={22} />,
                title: "Signing Reason",
                desc: "Record reason for signing this document.",
                enabled: signingReasonEnabled,
                toggle: () => setSigningReasonEnabled(!signingReasonEnabled),
              },
            ].map(({ icon, title, desc, enabled, toggle }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-indigo-600">{icon}</span>
                  <button
                    type="button"
                    onClick={toggle}
                    className={`w-11 h-6 rounded-full relative shrink-0 transition ${
                      enabled ? "bg-violet-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${
                        enabled ? "left-5" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
                  <p className="text-[12px] text-gray-500 mt-1">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* FULL WIDTH - COMPLIANCE BAR */}
          <div className="bg-indigo-50/60 border border-indigo-100/50 rounded-2xl px-5 py-3 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-start gap-3">
                <Info size={18} className="text-indigo-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-[13px] font-bold text-gray-900">Security & Compliance</h4>
                  <p className="text-[12px] text-gray-500 mt-0.5">
                    These advanced options ensure higher trust, non-repudiation and compliance with global standards.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6 sm:gap-8 self-end md:self-auto">
                {["NIST Compliant", "ETSI EN 319 142", "eIDAS", "ISO 27001"].map((label) => (
                  <div key={label} className="flex flex-col items-center gap-1.5 text-center">
                    <span className="text-[11px] font-medium text-gray-800 whitespace-nowrap">{label}</span>
                    <CheckCircle2 size={16} className="text-emerald-500 fill-emerald-500 text-white" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* NAVIGATION BUTTONS */}
          <div className="flex justify-between items-center pt-1">
            <button
              type="button"
              onClick={() =>
                navigate("/newdigital", {
                  state: {
                    ...state,
                    identity,
                    uploadedFile,
                    documentData,
                    uploadedFiles,
                    selectedDigitalId,
                  },
                })
              }
              className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition text-sm font-medium"
            >
              <ArrowLeft size={16} /> Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition text-sm font-medium shadow-sm"
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}